import type { ClassificationLevel } from "@esi/shared";
import { Shield } from "lucide-react";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAttachmentContext } from "./attachments/AttachmentControls.tsx";

/**
 * The classification levels the conversation's viewer knows, for the labels of cited sources:
 * the project settings' scheme, or the documents list's levels for guests (as `canOpen` reads
 * them). Whether a level gets the lock never depends on it: the server says so per citation.
 */
export const CitationSchemeContext = createContext<readonly ClassificationLevel[] | undefined>(
  undefined,
);

/**
 * A level id's label: from the conversation's scheme, else the attachment composer's complete
 * one (a standalone conversation has no project scheme); an id neither has shows as is.
 */
export function useClassificationLabel<T extends string | undefined>(
  id: T,
): T extends string ? string : null;
export function useClassificationLabel(id: string | undefined): string | null {
  const scheme = useContext(CitationSchemeContext);
  const composer = useAttachmentContext()?.list?.composer?.uploadPolicy.classifications;
  if (!id) {
    return null;
  }
  return scheme?.find((l) => l.id === id)?.label ?? composer?.find((l) => l.id === id)?.label ?? id;
}

/**
 * A cited source's classification: the neutral mark vocabulary (sunken, hairline, no state
 * colour), a shield and the level's label.
 */
export function ClassificationMark({ id, className }: { id: string; className?: string }) {
  const { t } = useTranslation();
  const label = useClassificationLabel(id);
  return (
    <span
      data-classification={id}
      title={t("citations.classification", { label })}
      className={cn(
        "inline-flex h-5 max-w-full items-center gap-1 rounded-[5px] border border-line bg-surface-sunken px-1.5 align-middle text-xs font-medium text-fg-secondary",
        className,
      )}
    >
      <Shield aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
      <span className="sr-only">{t("citations.classification", { label })}</span>
      <span aria-hidden="true" className="truncate">
        {label}
      </span>
    </span>
  );
}
